import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageTitle } from '../../contexts/PageTitleContext';
import Breadcrumbs from './Breadcrumbs';
import './PageHeader.scss';

/**
 * PageHeader Component - Page title with optional icon and actions
 * Matches UX_DESIGNS styling with theme support
 * 
 * Props:
 *   backButton  — boolean, renders an inline back arrow before the icon/title.
 *                 Defaults to on when `breadcrumbs` has a step above the page.
 *   onBack      — what the arrow does. Without it the arrow goes to the
 *                 breadcrumbs' parent, and only with neither to browser history.
 *   breadcrumbs — the trail down to this page, `[{ label, to, icon? }, …, { label }]`
 *                 (see ui/Breadcrumbs). Drawn above the page, and the arrow
 *                 goes up one step of it.
 */
const PageHeader = ({ 
  title, 
  description, 
  icon: Icon, 
  actions, 
  backButton,
  onBack,
  breadcrumbs,
  className = '' 
}) => {
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const parent = breadcrumbs?.length > 1 ? breadcrumbs[breadcrumbs.length - 2] : null;
  const handleBack = onBack || (parent ? () => navigate(parent.to) : () => navigate(-1));
  const showBack = backButton ?? !!parent;
  const backLabel = parent ? `Back to ${parent.label}` : 'Go back';
  const crumbs = parent ? <Breadcrumbs items={breadcrumbs} /> : null;

  // When the top nav exposes a left slot, the icon+title render there instead
  // of in the body. Falls back to in-body rendering when the slot is absent.
  const [navSlot, setNavSlot] = useState(() =>
    typeof document !== 'undefined' ? document.getElementById('top-nav-portal-left') : null
  );
  useEffect(() => {
    setNavSlot(document.getElementById('top-nav-portal-left'));
  }, []);

  useEffect(() => {
    setPageTitle(title || '');
    return () => setPageTitle('');
  }, [title, setPageTitle]);

  const titleBlock = (
    <>
      {showBack && (
        <button
          type="button"
          className="page-header__back-btn"
          onClick={handleBack}
          aria-label={backLabel}
          title={parent ? backLabel : undefined}
        >
          <ArrowLeft size={18} />
        </button>
      )}
      {Icon && (
        <div className="page-header__icon">
          <Icon size={20} />
        </div>
      )}
      <div className="page-header__text">
        <h1 className="page-header__title">{title}</h1>
        {description && (
          <p className="page-header__description">{description}</p>
        )}
      </div>
    </>
  );

  // Slot available: portal the icon+title into the top nav, keep only the
  // breadcrumbs and actions in the body (rendered only when present). The
  // breadcrumbs take the left of that row, which was empty space.
  if (navSlot) {
    return (
      <>
        {createPortal(
          <div className="page-header__nav-title">{titleBlock}</div>,
          navSlot
        )}
        {(actions || crumbs) && (
          <div className={`page-header page-header--compact ${className}`}>
            {crumbs || <span />}
            {actions && <div className="page-header__actions">{actions}</div>}
          </div>
        )}
      </>
    );
  }

  const header = (
    <div className={`page-header ${className}`}>
      <div className="page-header__left">{titleBlock}</div>
      {actions && (
        <div className="page-header__actions">
          {actions}
        </div>
      )}
    </div>
  );

  return crumbs ? <div className="page-header-stack">{crumbs}{header}</div> : header;
};

export default PageHeader;
